import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  useMedia,
  useMobileSimplifiedToothChartVisibility,
} from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { MobileLayout } from '@/shared/ui';

import { i18n, ReportLayout, ReportSettingsToggle } from '@/entities/reports';

import { MedicalImageToolbar } from '@/features/medicalImageToolbar';
import { MaskFiltersWrapper, MaskTooltip } from '@/features/renderMasks';
import { useReportCheck } from '@/features/reportCheck';
import { SimplifiedToothChart } from '@/features/simplifiedToothChart';

import { Conclusion } from '@/widgets/Conclusion';
import { MobileHeader } from '@/widgets/Header';
import { NonDentalFindings } from '@/widgets/NonDentalFindings';
import { Report2DToothChart } from '@/widgets/Report2DToothChart';
import { ReportActions } from '@/widgets/ReportActions';
import { ToothCardList } from '@/widgets/ToothCard';

import { ToothNumberEditPopup } from '@/pages/GpReport/ui/ToothNumberEditPopup';

import { PanoChangeTeethNumberModal } from './PanoChangeTeethNumberModal/PanoChangeTeethNumberModal';
import { PanoImageSlider } from './PanoImageSlider/PanoImageSlider';
import styles from './PanoReport.module.scss';
import { PanoReportRender } from './PanoReportRender/PanoReportRender';

export const PanoReport = () => {
  const { patientID, reportID = '' } = useParams();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const mobileToothChartStyle = styles.mobileToothChart;

  const { simplifiedChartRef, shouldShowMobileSimplifiedToothChart } =
    useMobileSimplifiedToothChartVisibility({
      mobileToothChartStyle,
    });

  useReportCheck(reportID);

  useEffect(() => {
    trackEvent({
      eventName: 'PanoReport Page Viewed',
      properties: {
        patient_id: patientID,
        report_id: reportID,
        report_type: ReportType.ReportType_Pano_GP,
      },
      isPageViewEvent: true,
    });
  }, []);

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader />

        {shouldShowMobileSimplifiedToothChart && (
          <div
            ref={simplifiedChartRef}
            className={styles.simplifiedToothChartWrapper}
          >
            <SimplifiedToothChart
              reportID={reportID}
              patientID={patientID ?? ''}
              useScroll
              mergePrimaryWithAdult
            />
          </div>
        )}
        <MobileLayout.Main>
          <PanoReportRender>
            <MaskFiltersWrapper className={styles.maskFilters} />
            <MaskTooltip />
            <MedicalImageToolbar
              toolbarID="main"
              className={styles.mobileToolbar}
            />
            <PanoImageSlider reportID={reportID as string} />
          </PanoReportRender>

          <ReportSettingsToggle />

          <Report2DToothChart className={mobileToothChartStyle} />

          <NonDentalFindings />
          <ToothCardList />
          <Conclusion />
        </MobileLayout.Main>
        <MobileLayout.Footer>
          <ReportActions
            className={styles.mobileReportActions}
            patientID={patientID as string}
            reportID={reportID as string}
          />
        </MobileLayout.Footer>

        {/* Modals */}
        <PanoChangeTeethNumberModal />
      </MobileLayout>
    );
  }

  return (
    <>
      <ReportLayout.LeftColumn data-section="pano-report">
        <h1>{formatMessage(i18n[ReportType.ReportType_Pano_GP])}</h1>
        <PanoReportRender>
          <ToothNumberEditPopup />

          <MaskFiltersWrapper className={styles.maskFilters} />
          <MaskTooltip />
          <MedicalImageToolbar toolbarID="main" />
          <PanoImageSlider reportID={reportID as string} />
        </PanoReportRender>
      </ReportLayout.LeftColumn>

      <ReportLayout.RightColumn data-section="pano-report">
        <Report2DToothChart />

        <NonDentalFindings />

        <ToothCardList />

        <Conclusion />

        <ReportActions
          className={styles.reportActions}
          patientID={patientID as string}
          reportID={reportID as string}
        />
      </ReportLayout.RightColumn>

      {/* Modals */}
    </>
  );
};
